import { V2SearchTypes } from './analytics_search.type'
import { AnalyticsMode, ExternalType, SearchType, WorkspaceScope } from './sse.type'

export interface Stock {
  id: string
  ticker: string
  name?: string
  currency?: string
  exchange?: string
  countryCode?: string // Legacy field for backward compatibility
  country?: string // New field from backend
  ticker_region_factset_direct?: string // FactSet ticker-region, e.g., "NOVO.B-DK"
}

interface Document {
  id: string
  name: string
  doc_type: string
  stock_id: string
}

interface Date {
  s_date: string
  e_date?: string // Today는 e_date가 없음
}

export interface SearchFilter {
  stocks?: Stock[]
  documents?: Document[]
  // artifact_chat: the shared artifact a Q&A conversation is about. Unlike the
  // other keys this is conversation-level identity, not a search scope — the
  // server looks a conversation up by it (GET /conversations/for_artifact).
  artifact_id?: string
  calendar_date?: string[]
  fiscal_date?: string[]
  date?: Date
  region?: string
  doc_source?: string[]
  // Analytics fundamental-data vendor selection (S&P Capital IQ / FactSet).
  // Same encoding as doc_source: undefined = not specified, [] = all deselected.
  data_vendors?: string[]
  // Analytics Meeting Agent source toggle (soft, DPC prompt-level). undefined =
  // omit (no MA access), true = ON (include in search), false = OFF/excluded.
  meeting_agent_enabled?: boolean
  // Analytics Web Search toggle. false = OFF (web tools denied); true/undefined = ON.
  web_search_enabled?: boolean
  // Analytics Transcript sub-types that are on (TRANSCRIPT_TYPE_OPTIONS values).
  transcript_types?: string[]
  selected_ontology_entity_id?: string
  source_id?: string[]
  industry?: string[]
  country?: string[]
  stock_ids?: string[]
  market_cap_max?: number
  market_cap_min?: number
  function_content?: string
  index_id?: string
  file_key?: string
  source_name?: string
  metric_list?: { name: string; desc: string }[]
  metric?: { name: string; desc: string; operator: string; value: number }[]
  ticker_name?: string // primer filters
  theme?: string // -- pulse check filters --
  ticker?: string[]
  indices?: string[]
  category?: string
  start_date?: string
  end_date?: string
  industries?: string[]
  keywords_of_mention?: string // -- pulse check filters --
  user_source_ids?: string[] // TFS에서 업로드한 파일 리스트
  search_types?: SearchType[] // RMSV2
  external_types?: ExternalType[] // RMSV2
  workspace_scopes?: WorkspaceScope[] | null // RMSV2
  search_types_v2?: V2SearchTypes // V2 구조 (신규)
  mode?: AnalyticsMode // analytics 실행 모드 (storage redirect 시 AnalyticsMode.Storage)
  template_variables?: Record<string, string> // analytics DPC Jinja substitution (company_deepdive 등)
  is_follow_up_question?: boolean
  date_range?: 'week' | 'month' // Reddit Report
  company?: string // Reddit Report
  topic?: string // Reddit Report
  research_focus?: 'customer_perspective' // Reddit Report
  system_prompt?: string // TFS filter system prompt text
  credit_rv_payload?: {
    // Credit Agent relative-value payload — carried via filter so message content
    // can stay as a natural-language prompt while the agent still receives the
    // structured stock reference.
    id: string
    ticker: string
    name: string
    fsym_regional_id: string
  }
}

/**
 * UI에 영향을 주는것들의 모음집
 * Collection of values that affect the UI — used for restoring filter state on conversation re-entry.
 * These values do NOT affect search directly; only system_prompt / search_types_v2 do.
 *
 * Mirrors a subset of `SearchFilter` keys with the same schema (TFS pattern):
 *   - filters.stocks         → filter_metadata.stocks
 *   - filters.metric_list    → filter_metadata.metric_list
 *   - filters.doc_source     → filter_metadata.doc_source
 *   - filters.search_types_v2 → filter_metadata.search_types_v2  (RMS)
 *   - filters.meeting_agent_enabled → filter_metadata.meeting_agent_enabled  (MA toggle)
 *
 * On conversation re-entry the page restore effects pass this snapshot to
 * the search source store so SourcesPanel UI lands on the same selection
 * the user had on send — independent of what the live search query carried.
 */
export interface FilterMetadata {
  selected_ontology_entity_id?: string
  stocks?: Stock[]
  metric_list?: { name: string; desc: string }[]
  doc_source?: string[]
  data_vendors?: string[]
  meeting_agent_enabled?: boolean
  web_search_enabled?: boolean
  // Transcript sub-types left on. Written only when one of them is off; absent = all on.
  transcript_types?: string[]
  // Apps (live connectors) turned off in the Sources panel. Absent = all on.
  disabled_connectors?: string[]
  search_types_v2?: V2SearchTypes
  /** Model choice on the conversation's first workspace turn: "auto" or the
   *  picked model id. Written once, read only for the Auto label. */
  workspace_model_selection?: string
}

export enum DateFilterOption {
  Today = 'Today',
  PastWeek = 'Past Week',
  PastMonth = 'Past Month',
}

export enum RegionCode {
  US = 'us',
  JP = 'jp',
  CN = 'cn',
  TW = 'tw',
  KR = 'kr',
}

export type Region = {
  name: string
  code: RegionCode
}
